import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle, Clock, Lock, Mail, Pencil, Check, X, Loader2 } from 'lucide-react';
import useQuizCodeStore from '../../store/quizCodeStore';
import RichContent from '../content/RichContent';
import QuestionCard from '../quiz/QuestionCard';
import ProgressBar from '../ui/ProgressBar';

const COLORS = { good: '#28a745', ok: '#e67e22', bad: '#dc3545' };
const getColor = (pct) => pct >= 75 ? COLORS.good : pct >= 50 ? COLORS.ok : COLORS.bad;
const INACTIVITY_DELAY = 60_000; // 60 seconds

export default function QuizCodePlayer({ onFinished, onBack, hideEmailEditor = false }) {
    const {
        code, sousSujet, design, mode, questions, answers, correctAnswers, answersSnapshot,
        currentIndex, timeInSeconds, finished, sealed, score, total, loading,
        email, emailLocked,
        setAnswer, nextQuestion, prevQuestion, goToQuestion, tick, finishQuiz, updateEmail,
    } = useQuizCodeStore();

    const isApprentissage = mode === 'apprentissage';
    const [paused, setPaused] = useState(false);

    // Participant email (opt-in notification) — editable on solo quiz-codes only
    const [emailEditing, setEmailEditing] = useState(false);
    const [emailDraft, setEmailDraft] = useState('');
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailError, setEmailError] = useState(null);

    const saveEmail = async () => {
        const val = emailDraft.trim();
        if (val && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) {
            setEmailError('Email invalide (ou laissez vide pour retirer)');
            return;
        }
        setEmailSaving(true);
        setEmailError(null);
        const res = await updateEmail(val);
        setEmailSaving(false);
        if (res.success) {
            setEmailEditing(false);
        } else {
            setEmailError(res.error || 'Erreur');
        }
    };

    const renderEmailRow = () => {
        if (hideEmailEditor) return null;
        return (
            <div className="qc-email-row">
                <Mail size={13} />
                {emailEditing ? (
                    <>
                        <input
                            type="email"
                            value={emailDraft}
                            onChange={e => setEmailDraft(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); saveEmail(); }
                                if (e.key === 'Escape') { e.preventDefault(); setEmailEditing(false); setEmailError(null); }
                            }}
                            placeholder="votre@email.fr"
                            className="qc-email-input"
                            autoFocus
                            disabled={emailSaving}
                            maxLength={255}
                        />
                        <button className="qc-email-btn qc-email-btn--save" onClick={saveEmail} disabled={emailSaving}>
                            {emailSaving ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
                        </button>
                        <button className="qc-email-btn qc-email-btn--cancel" onClick={() => { setEmailEditing(false); setEmailError(null); }} disabled={emailSaving}>
                            <X size={12} />
                        </button>
                    </>
                ) : (
                    <>
                        {email
                            ? <span className="qc-email-value">{email}</span>
                            : <span className="qc-email-placeholder">Ajouter un email pour recevoir les r&eacute;sultats</span>
                        }
                        {!emailLocked && (
                            <button className="qc-email-btn" onClick={() => { setEmailDraft(email || ''); setEmailEditing(true); }} title={email ? 'Modifier' : 'Ajouter'}>
                                <Pencil size={11} />
                            </button>
                        )}
                        {emailLocked && (
                            <span className="qc-email-locked" title="Adresse fournie par le formateur"><Lock size={10} /></span>
                        )}
                    </>
                )}
                {emailError && <span className="qc-email-error">{emailError}</span>}
            </div>
        );
    };

    const headerImage = design?.header_image;
    const categoryImage = design?.category_image;
    const themeColor = design?.couleur_theme || '#7c3aed';
    const scopeLabel = design?.scope_label;

    const timerRef = useRef(null);
    const dotsRef = useRef(null);
    const inactivityRef = useRef(null);

    // Timer — pauses when inactive
    useEffect(() => {
        if (!loading && !finished && questions.length > 0 && !paused) {
            timerRef.current = setInterval(() => tick(), 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [finished, loading, questions.length, paused, tick]);

    // Inactivity detection — pause after 60s of no interaction
    useEffect(() => {
        if (loading || finished || questions.length === 0) return;

        const resetInactivity = () => {
            if (paused) setPaused(false);
            clearTimeout(inactivityRef.current);
            inactivityRef.current = setTimeout(() => setPaused(true), INACTIVITY_DELAY);
        };

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(e => document.addEventListener(e, resetInactivity, { passive: true }));
        inactivityRef.current = setTimeout(() => setPaused(true), INACTIVITY_DELAY);

        return () => {
            events.forEach(e => document.removeEventListener(e, resetInactivity));
            clearTimeout(inactivityRef.current);
        };
    }, [loading, finished, questions.length]);

    // Save before page close/refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!finished && questions.length > 0) {
                useQuizCodeStore.getState()._autoSave();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [finished, questions.length]);

    // Body scroll is handled by quiz-modal-overlay

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    const currentQuestion = questions[currentIndex];
    const allAnswered = questions.length > 0 && questions.every(q => answers[q.id] != null);
    const autoFinishRef = useRef(false);

    const handleFinish = async () => {
        const ok = await finishQuiz();
        if (ok && onFinished) onFinished();
    };

    // Auto-finish in apprentissage mode when all questions answered + all corrections received
    useEffect(() => {
        if (!isApprentissage || !allAnswered || finished || loading || autoFinishRef.current) return;
        // Check that all corrections have been received
        const allCorrected = questions.every(q => correctAnswers?.[q.id]);
        if (!allCorrected) return;
        autoFinishRef.current = true;
        // Wait 3s so the user can read the last feedback
        const timer = setTimeout(() => handleFinish(), 3000);
        return () => clearTimeout(timer);
    }, [isApprentissage, allAnswered, finished, loading, questions, correctAnswers]);

    const scrollDots = (ref, dir) => {
        if (!ref.current) return;
        ref.current.scrollBy({ left: dir === 'left' ? -120 : 120, behavior: 'smooth' });
    };

    if (loading) {
        return <div className="quiz-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div className="loading">Chargement...</div>
        </div>;
    }

    // === SEALED SCREEN (results locked by trainer) ===
    if (finished && sealed) {
        return (
            <div className="quiz-page">
                {(headerImage || scopeLabel) && (
                    <div className="quiz-scope-banner" style={themeColor ? { '--scope-accent': themeColor } : {}}>
                        {headerImage && (
                            <div className="quiz-scope-banner-hero">
                                <img src={headerImage} alt="" className="quiz-scope-banner-img" />
                                <div className="quiz-scope-banner-gradient" />
                            </div>
                        )}
                        <div className="quiz-scope-banner-content">
                            <div className="quiz-scope-banner-info">
                                <span className="quiz-scope-banner-title">{scopeLabel}</span>
                            </div>
                            {sousSujet?.categorie && (
                                <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>
                            )}
                        </div>
                    </div>
                )}
                <div className="quiz-sealed-wrap">
                    <div className="quiz-sealed-icon"><Lock size={44} strokeWidth={1.5} /></div>
                    <h2 className="quiz-sealed-title">Session enregistrée</h2>
                    <p className="quiz-sealed-sub">
                        Vos réponses ont bien été enregistrées pour le sujet <strong>{sousSujet?.label}</strong>.
                    </p>
                    <p className="quiz-sealed-sub">
                        Les résultats et la correction sont <strong>scellés</strong>. Ils vous seront communiqués ultérieurement.
                    </p>
                    {renderEmailRow()}
                    {onBack && (
                        <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={onBack}>
                            Fermer
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // === RESULTS SCREEN ===
    if (finished) {
        const pct = total > 0 ? Math.round((score / total) * 100) : 0;
        return (
            <div className="quiz-page">
                {(headerImage || scopeLabel) && (
                    <div className="quiz-scope-banner" style={themeColor ? { '--scope-accent': themeColor } : {}}>
                        {headerImage && (
                            <div className="quiz-scope-banner-hero">
                                <img src={headerImage} alt="" className="quiz-scope-banner-img" />
                                <div className="quiz-scope-banner-gradient" />
                            </div>
                        )}
                        <div className="quiz-scope-banner-content">
                            <div className="quiz-scope-banner-info">
                                <span className="quiz-scope-banner-title">{scopeLabel}</span>
                            </div>
                            {sousSujet?.categorie && (
                                <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>
                            )}
                        </div>
                    </div>
                )}

                <div style={{ textAlign: 'center', padding: '24px 16px 16px' }}>
                    <div style={{ fontSize: '3em', fontWeight: 800, color: getColor(pct), fontFamily: 'var(--font-display)' }}>{pct}%</div>
                    <div style={{ fontSize: '1.2em', color: 'var(--text-muted)', marginBottom: 8 }}>{score} / {total} bonnes r&eacute;ponses</div>
                    <div style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginBottom: 12 }}><Clock size={14} style={{ verticalAlign: 'middle' }} /> {formatTime(timeInSeconds)}</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>{renderEmailRow()}</div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 16px 24px', maxWidth: 700, margin: '0 auto' }}>
                    {questions.map((q, i) => {
                        const snap = answersSnapshot?.[q.id] || answersSnapshot?.[String(q.id)] || {};
                        const given = snap.given;
                        const correct = snap.correct;
                        const isCorrect = given === correct;
                        const ca = correctAnswers?.[q.id] || correctAnswers?.[String(q.id)];
                        return (
                            <div key={q.id} className="question-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.85em', color: 'var(--text-muted)' }}>Q{i + 1}</span>
                                    {isCorrect
                                        ? <CheckCircle size={18} style={{ color: COLORS.good }} />
                                        : <XCircle size={18} style={{ color: COLORS.bad }} />
                                    }
                                </div>
                                <div className="question-enonce"><RichContent blocks={q.enonce} /></div>
                                <div className="answers-list">
                                    {(q.reponses || []).map(r => {
                                        let cls = 'answer-btn';
                                        if (r.id === correct) cls += ' correct';
                                        else if (r.id === given && !isCorrect) cls += ' incorrect';
                                        return (
                                            <button key={r.id} className={cls} disabled>
                                                <span className="answer-label">{r.id.toUpperCase()}.</span>
                                                <div className="answer-content"><RichContent blocks={r.content} /></div>
                                            </button>
                                        );
                                    })}
                                </div>
                                {ca?.explication && (
                                    <div className="quiz-explication"><RichContent blocks={ca.explication} /></div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div style={{ textAlign: 'center', padding: '0 16px 32px' }}>
                    <button className="btn btn-primary" onClick={onBack}>Fermer</button>
                </div>
            </div>
        );
    }

    // === QUIZ SCREEN ===
    if (!currentQuestion) return null;

    return (
        <>
        <div className="quiz-page no-select">
            {/* Scope banner */}
            {scopeLabel && (
                <div className="quiz-scope-banner" style={themeColor ? { '--scope-accent': themeColor } : {}}>
                    {headerImage ? (
                        <div className="quiz-scope-banner-hero">
                            <img src={headerImage} alt="" className="quiz-scope-banner-img" />
                            <div className="quiz-scope-banner-gradient" />
                        </div>
                    ) : (
                        <div className="quiz-scope-banner-hero" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)`, minHeight: 60 }} />
                    )}
                    <div className="quiz-scope-banner-content">
                        <div className="quiz-scope-banner-info">
                            <span className="quiz-scope-banner-title">{scopeLabel}</span>
                        </div>
                        {sousSujet?.categorie && (
                            <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>
                        )}
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="quiz-header">
                <div className="quiz-header-top">
                    <h2>{sousSujet?.label || 'Quiz'}</h2>
                    <span className={`quiz-mode-badge ${mode}`}>{isApprentissage ? 'Apprentissage' : 'Classique'}</span>
                </div>
                <div className="quiz-meta">
                    <span className="quiz-timer">{formatTime(timeInSeconds)}</span>
                    <ProgressBar current={currentIndex + 1} total={questions.length} />
                </div>
            </div>

            {/* Mini-nav dots */}
            <div className="quiz-mini-nav">
                <button className="mini-nav-arrow" onClick={() => scrollDots(dotsRef, 'left')}>
                    <ChevronLeft size={18} />
                </button>
                <div className="mini-nav-dots" ref={dotsRef}>
                    {questions.map((q, i) => {
                        const hasAnswer = !!answers[q.id];
                        const isCurrent = i === currentIndex;
                        const ca = isApprentissage && hasAnswer ? correctAnswers?.[q.id] : null;
                        const isCorrect = ca && ca.correct === answers[q.id];
                        const isIncorrect = ca && ca.correct !== answers[q.id];
                        return (
                            <button key={q.id}
                                className={`dot ${hasAnswer ? 'answered' : ''} ${isCurrent ? 'active' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''}`}
                                onClick={() => goToQuestion(i)}>
                                {i + 1}
                            </button>
                        );
                    })}
                </div>
                <button className="mini-nav-arrow" onClick={() => scrollDots(dotsRef, 'right')}>
                    <ChevronRight size={18} />
                </button>
            </div>

            {/* Question — reuse existing QuestionCard */}
            {(() => {
                const selectedAnswer = answers[currentQuestion.id];
                const hasAnswered = !!selectedAnswer;
                const showFeedback = isApprentissage && hasAnswered && correctAnswers?.[currentQuestion.id];
                const correct = correctAnswers?.[currentQuestion.id]?.correct;
                return (
                    <>
                    <QuestionCard
                        question={currentQuestion}
                        selectedAnswer={selectedAnswer}
                        onAnswer={(id) => setAnswer(currentQuestion.id, id)}
                        showResult={!!showFeedback}
                        correctAnswer={correct}
                        apprentissageFeedback={isApprentissage}
                    />
                    {showFeedback && correctAnswers[currentQuestion.id]?.explication && (
                        <div className="quiz-explication" style={{ margin: '0 0 16px' }}>
                            <RichContent blocks={correctAnswers[currentQuestion.id].explication} />
                        </div>
                    )}
                    </>
                );
            })()}

            {/* Navigation */}
            <div className="quiz-nav">
                <button className="btn btn-ghost btn-sm" onClick={prevQuestion} disabled={currentIndex === 0}>
                    <ChevronLeft size={16} /> Pr&eacute;c&eacute;dente
                </button>
                {allAnswered ? (
                    <button className="btn btn-success" onClick={handleFinish}>Terminer</button>
                ) : currentIndex < questions.length - 1 ? (
                    <button className="btn btn-primary quiz-next-btn" onClick={nextQuestion}>
                        Question suivante <ChevronRight size={16} />
                    </button>
                ) : null}
            </div>

            {/* Pause overlay on inactivity */}
            {paused && !finished && (
                <div className="quiz-pause-overlay" onClick={() => setPaused(false)}>
                    <div className="quiz-pause-content">
                        <div className="quiz-pause-icon">⏸</div>
                        <h3>Quiz en pause</h3>
                        <p>Le timer est arr&ecirc;t&eacute; suite &agrave; une inactivit&eacute;.</p>
                        <button className="btn btn-primary" onClick={() => setPaused(false)}>Reprendre</button>
                    </div>
                </div>
            )}
        </div>

        {/* Side arrows */}
        {questions.length > 1 && (
            <>
                <button className="quiz-nav-arrow quiz-nav-arrow--left" onClick={prevQuestion} disabled={currentIndex === 0}>
                    <ChevronLeft size={28} />
                </button>
                <button className="quiz-nav-arrow quiz-nav-arrow--right" onClick={currentIndex < questions.length - 1 ? nextQuestion : undefined} disabled={currentIndex >= questions.length - 1}>
                    <ChevronRight size={28} />
                </button>
            </>
        )}
        </>
    );
}
